import { useContentAccessStore } from '@/content_access/content_access.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { CONTENT_TYPES, savedContentTypeMapping } from '@/util/access_visibility'

interface FolderContentQuota {
  quotaName: string
  pluralLabel: string
  singularLabel: string
}

const GOAL_QUOTA: FolderContentQuota = {
  quotaName: 'max_goals_per_student',
  pluralLabel: 'goals',
  singularLabel: 'goal'
}

const FOLDER_CONTENT_QUOTAS: Record<string, FolderContentQuota> = {
  [CONTENT_TYPES.GOAL]: GOAL_QUOTA,
  [CONTENT_TYPES.SUPER_PAGE]: GOAL_QUOTA,
  [CONTENT_TYPES.SAVED_SUPER_PAGE]: GOAL_QUOTA,
  [CONTENT_TYPES.STRATEGY]: {
    quotaName: 'max_strategies_per_student',
    pluralLabel: 'strategies',
    singularLabel: 'strategy'
  },
  [CONTENT_TYPES.RESOURCE]: {
    quotaName: 'max_resources_per_student',
    pluralLabel: 'downloadable resources',
    singularLabel: 'resource'
  },
  [CONTENT_TYPES.PRESENTLEVEL]: {
    quotaName: 'max_presentlevels_per_student',
    pluralLabel: 'present levels',
    singularLabel: 'present levels'
  }
}

export function useFolderContentQuota() {
  const contentAccessStore = useContentAccessStore()
  const analytics = getAnalyticsTracker()

  function folderQuotaErrorMessage(contentType: string, folderUuid: string): string | null {
    const quota = FOLDER_CONTENT_QUOTAS[contentType]
    if (!quota || !contentAccessStore.maxContentReachedForFolder(contentType, folderUuid)) {
      return null
    }

    const savedContentType = savedContentTypeMapping[contentType] || contentType
    analytics.sendEvent('Content Access Blocked', {
      blocked_action: `Save ${contentType} to Library`,
      blocking_quota: quota.quotaName,
      blocking_quota_value: contentAccessStore.libraryQuotaForContentType(savedContentType),
      student_uuid: folderUuid
    })

    return `You've reached the maximum number of ${quota.pluralLabel} for this student folder. You may delete an existing ${quota.singularLabel} to add a new one.`
  }

  return { folderQuotaErrorMessage }
}
